/**
 * Pivot helpers for the "Seri kırılımı" feature.
 *
 * The worker returns rows shaped like:
 *   `{ <primary>: any, <breakdown>: any, metric: number }`
 *
 * Bar / Line charts need a categorical view:
 *   `categories: ['Jan', 'Feb', ...]`
 *   `series:     [{ name: 'Drug A', data: [...] }, { name: 'Drug B', ... }]`
 *
 * Both axes are truncated to top-N:
 *   - primary categories: top N by total metric across all breakdown values
 *   - breakdown series:    top N by total metric across all primary values
 * with the remaining tail dropped (no implicit "Other" bucket). Sorting:
 *   - primary categories preserve source order when the primary column
 *     is a date / time axis (the SQL already returns chronological);
 *     otherwise sorted descending by total metric so the heaviest bars /
 *     lines come first.
 *   - breakdown series sorted descending by total metric so the legend
 *     reads top-to-bottom by impact.
 *
 * Pure module — no React / ECharts deps so the tests can lock the
 * contract independently of the chart engine.
 */

import type { QueryResultRow } from '@/types/query';
import { categoryKey } from '@/lib/date-format';

export type BreakdownPivot = {
  /** Distinct primary-axis values in the chosen display order. */
  categories: unknown[];
  /** One series per top-N breakdown value, in legend display order. */
  series: Array<{ name: string; data: number[] }>;
};

export type BreakdownPivotOptions = {
  /** Cap on the primary category axis. Falsy / Infinity → no cap. */
  primaryLimit?: number;
  /** Cap on the breakdown series count. Defaults to 8. */
  breakdownLimit?: number;
  /**
   * Set when the primary axis is a date / time column. Categories
   * keep their source (chronological) order; otherwise they sort by
   * total metric descending.
   */
  preserveCategoryOrder?: boolean;
};

/**
 * Pivot multi-group rows into a categories + series layout. Missing
 * cells default to zero so the chart shows a flat baseline rather than
 * a hole — same convention as compare-mode pivot.
 */
export function pivotBreakdownRows(
  rows: readonly QueryResultRow[],
  primaryCol: string,
  breakdownCol: string,
  metricAlias: string,
  opts?: BreakdownPivotOptions,
): BreakdownPivot {
  const primaryLimit = opts?.primaryLimit ?? Infinity;
  const breakdownLimit = opts?.breakdownLimit ?? 8;
  const preserveOrder = opts?.preserveCategoryOrder ?? false;

  // Track per-primary and per-breakdown totals + the (p, b) → value map.
  const primaryTotals = new Map<string, number>();
  const breakdownTotals = new Map<string, number>();
  // First-seen order of primary categories (used when preserveOrder=true).
  const primarySeen: string[] = [];
  const primaryRaw = new Map<string, unknown>();
  const breakdownSeen: string[] = [];
  const breakdownRaw = new Map<string, unknown>();
  const cell = new Map<string, number>();

  for (const row of rows) {
    const pRaw = row[primaryCol];
    const bRaw = row[breakdownCol];
    const p = categoryKey(pRaw);
    const b = categoryKey(bRaw);
    const v = toNumber(row[metricAlias]);

    if (!primaryRaw.has(p)) {
      primaryRaw.set(p, pRaw);
      primarySeen.push(p);
    }
    if (!breakdownRaw.has(b)) {
      breakdownRaw.set(b, bRaw);
      breakdownSeen.push(b);
    }
    primaryTotals.set(p, (primaryTotals.get(p) ?? 0) + v);
    breakdownTotals.set(b, (breakdownTotals.get(b) ?? 0) + v);
    cell.set(`${p} ${b}`, (cell.get(`${p} ${b}`) ?? 0) + v);
  }

  // Pick top-N breakdown series by total descending. Ties broken by
  // first-seen order so the result is deterministic across runs.
  const breakdownOrdered = [...breakdownSeen].sort((a, b) => {
    const diff = (breakdownTotals.get(b) ?? 0) - (breakdownTotals.get(a) ?? 0);
    return diff !== 0 ? diff : breakdownSeen.indexOf(a) - breakdownSeen.indexOf(b);
  });
  const topBreakdowns = breakdownOrdered.slice(0, breakdownLimit);

  // Pick the primary categories. When preserving order (time axis), we
  // keep first-seen order then truncate the tail. Otherwise sort by
  // total metric descending.
  const primaryOrdered = preserveOrder
    ? [...primarySeen]
    : [...primarySeen].sort((a, b) => {
        const diff = (primaryTotals.get(b) ?? 0) - (primaryTotals.get(a) ?? 0);
        return diff !== 0 ? diff : primarySeen.indexOf(a) - primarySeen.indexOf(b);
      });
  const topPrimaries = Number.isFinite(primaryLimit)
    ? primaryOrdered.slice(0, primaryLimit)
    : primaryOrdered;

  const categories = topPrimaries.map((p) => primaryRaw.get(p));

  const series = topBreakdowns.map((b) => ({
    name: String(breakdownRaw.get(b) ?? b),
    data: topPrimaries.map((p) => cell.get(`${p} ${b}`) ?? 0),
  }));

  return { categories, series };
}

function toNumber(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'bigint') return Number(v);
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}
